import { jsx as _jsx } from "react/jsx-runtime";
import { Children, cloneElement, Fragment, isValidElement, useContext, useEffect, useId, useMemo, useRef, useState, } from 'react';
import { Dropdown as AntdDropdown, Popconfirm as AntdPopconfirm, Popover as AntdPopover, Tooltip as AntdTooltip, } from 'antd';
import { Icon } from '../icons/icon.js';
import { resolveDropdownPlacement, resolvePlacement, useUiDirection } from './direction.js';
import { inputModality } from './input-modality.js';
import { PopupTriggerContext, PopupTriggerProvider, useTooltipGroup, useTooltipGroupAttachment, } from './tooltip-context.js';
import { LARGE_TRIGGER_AREA, REST_TOLERANCE_PX } from './tooltip-group.js';
export { useTooltipSuppression } from './tooltip-context.js';
/**
 * ════════════════════════════════════════════════════════════════════════════
 *  OVERLAYS — anchored to a logical edge, drawn with the shared icon set
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Everything here exists for one reason: `placement` in AntD is physical, and
 * physical placement is a bug in a product that is only ever RTL. See
 * `./direction.ts` for the whole argument. The wrappers take logical names and
 * resolve them; there is no way to pass `bottomLeft` through, which is the
 * point.
 *
 * The second job is the submenu chevron. AntD picks `LeftOutlined` for RTL by
 * itself — correct, but drawn from `@ant-design/icons`, a second icon set in a
 * product that has one. `menu.expandIcon` replaces it with the registry's
 * `chevron-forward`, which resolves the same way for the same reason.
 *
 * The third job is the TOOLTIP: one timing contract for the whole product
 * (`./tooltip-group.ts`), touch-safe, keyboard-reachable, and quiet while the
 * control's own menu or dialog is open. `Dropdown`, `Popover` and `Popconfirm`
 * therefore mirror their open state into `PopupTriggerContext` (see
 * `./tooltip-context.tsx`).
 */
/** `trigger` includes a click-like action (the overlay is a real popup the user opened). */
function isClickTrigger(trigger, fallback) {
    const list = trigger === undefined ? [fallback] : Array.isArray(trigger) ? trigger : [trigger];
    return list.includes('click') || list.includes('contextMenu');
}
/** Wrap overlay content so a tooltip INSIDE the open popup is not treated as its trigger. */
function isolate(node) {
    return _jsx(PopupTriggerProvider, { value: null, children: node });
}
function isolateRender(content) {
    if (typeof content === 'function') {
        return (() => isolate(content()));
    }
    if (content === undefined || content === null || content === false)
        return content;
    return isolate(content);
}
/** The overlay's open state, mirrored whether the caller controls it or not. */
function useMirroredOpen(controlled, defaultOpen) {
    const [inner, setInner] = useState(Boolean(defaultOpen));
    return [controlled ?? inner, setInner];
}
/**
 * A menu hung off a trigger.
 *
 * The submenu arrow is a directional registry icon, so a nested menu in RTL
 * points toward where the submenu will actually appear. While the menu is
 * open the trigger's tooltip is quiet, and a kit button trigger announces
 * `aria-haspopup="menu"` + `aria-expanded`.
 */
export function Dropdown({ placement = 'bottom-start', menu, onOpenChange, popupRender, dropdownRender, ...rest }) {
    const dir = useUiDirection();
    const [open, setOpen] = useMirroredOpen(rest.open, undefined);
    const mergedMenu = menu
        ? {
            expandIcon: _jsx(Icon, { name: "chevron-forward", size: 16 }),
            ...menu,
        }
        : menu;
    const render = popupRender ?? dropdownRender;
    return (_jsx(PopupTriggerProvider, { value: { open, haspopup: 'menu', clickTrigger: isClickTrigger(rest.trigger, 'hover') }, children: _jsx(AntdDropdown, { ...rest, menu: mergedMenu, placement: resolveDropdownPlacement(placement, dir), onOpenChange: (next, info) => {
                setOpen(next);
                onOpenChange?.(next, info);
            }, popupRender: (node) => isolate(render ? render(node) : node) }) }));
}
/** A touch/pen hold this long opens the bubble. */
export const LONG_PRESS_MS = 500;
/** A finger that travels further than this is scrolling or dragging, not holding. */
export const LONG_PRESS_SLOP_PX = 10;
/** A long-pressed bubble closes this long after the finger lifts. */
export const LONG_PRESS_LINGER_MS = 1500;
function distance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
}
/** Big enough that a warm pass across it should not open it (see `LARGE_TRIGGER_AREA`). */
function isLargeTrigger(el) {
    const rect = el.getBoundingClientRect();
    return rect.width * rect.height >= LARGE_TRIGGER_AREA;
}
const NO_MOTION = { motionName: '' };
function isEmptyTitle(title) {
    return title === undefined || title === null || title === false || title === '';
}
function isElementNode(value) {
    return Boolean(value) && typeof value === 'object' && value.nodeType === 1;
}
/**
 * Component types already reported by the dev assertion. Module-level so one
 * broken component is one message, not one per instance.
 */
const reportedTriggerTypes = new Set();
/**
 * Warn once per component type whose instance does not forward its ref/DOM
 * props: rc-trigger then never sees the element, and the tooltip can never
 * open. This is what hid the `Numeric` bug — every `Money short` value tooltip
 * was dead. Unconditional (rules of hooks) and env-free (the kit is built with
 * plain `tsc` and `types: []`); the check is one property read.
 */
function useDevAssertTriggerResolves(ref, child) {
    const type = child.type;
    useEffect(() => {
        if (isElementNode(ref.current?.nativeElement))
            return;
        if (reportedTriggerTypes.has(type))
            return;
        reportedTriggerTypes.add(type);
        console.error('[Tooltip] child does not forward ref/DOM props — wrap it in <span> or fix the component', type);
    }, [ref, type]);
}
/**
 * The kit tooltip. AntD renders it (placement, flip, arrow, `role="tooltip"`,
 * a hoverable bubble, z-index tokens, dark mode); the kit's delay group owns
 * WHEN it opens.
 *
 * - Hover (mouse only): after the pointer RESTS for 600 ms (`help`: 300 ms)
 *   — moving more than 6 px restarts the wait — or at once while another
 *   tooltip is open or just closed (a large trigger: after a 150 ms rest).
 *   Touch and pen never open a hover tooltip.
 * - Touch/pen hold (`label` / `description`): 500 ms with the finger still
 *   opens it, pinned, and swallows the click that follows, so the action does
 *   not run; it closes 1.5 s after the finger lifts, or on a tap elsewhere.
 *   A tap still opens nothing. `help` kinds pin on a plain tap instead.
 * - Keyboard focus from a navigation key: at once. A click's focus, or focus
 *   returned after Esc/Enter: never.
 * - Pressing the control dismisses its tooltip until the pointer leaves.
 * - Esc closes it through AntD's portal Esc stack (inside a Modal, the first
 *   Esc closes the tooltip and the second the Modal).
 * - Quiet while the control's own Dropdown/Popover/Popconfirm is open, and
 *   while a modal-ish surface called `useTooltipSuppression(true)`.
 *
 * It never adds a DOM wrapper: the child must forward its ref and DOM props
 * (a dev assertion names the component that does not).
 */
export function Tooltip({ placement = 'top', kind = 'description', title, children, tapToPin = false, clickToPin = false, shouldOpen, longPress = true, rootClassName, ...rest }) {
    const dir = useUiDirection();
    const group = useTooltipGroup();
    useTooltipGroupAttachment(group);
    const ownerPopupOpen = useContext(PopupTriggerContext)?.open ?? false;
    const id = useId();
    const antdRef = useRef(null);
    const [phase, setPhase] = useState('closed');
    const phaseRef = useRef('closed');
    const suppressed = useRef(false);
    const pinned = useRef(false);
    const empty = isEmptyTitle(title);
    // Rest detection: where the pointer is, and where the pending timer started.
    const pointerAt = useRef(null);
    const restAnchor = useRef(null);
    // Long-press: the running hold, where it started, whether it fired, and the linger.
    const holdTimer = useRef(null);
    const holdStart = useRef(null);
    const longPressed = useRef(false);
    const lingerTimer = useRef(null);
    const holdable = longPress && !tapToPin;
    const member = useMemo(() => ({
        id,
        show: (instant) => {
            phaseRef.current = instant ? 'instant-open' : 'delayed-open';
            setPhase(phaseRef.current);
        },
        hide: () => {
            pinned.current = false;
            phaseRef.current = 'closed';
            setPhase('closed');
        },
        triggerEl: () => {
            const el = antdRef.current?.nativeElement;
            return isElementNode(el) ? el : null;
        },
        popupEl: () => {
            const el = antdRef.current?.popupElement;
            return isElementNode(el) ? el : null;
        },
    }), [id]);
    const close = () => {
        group.cancel(member);
        if (phaseRef.current !== 'closed') {
            member.hide();
            group.release(member);
        }
    };
    const cancelHold = () => {
        if (holdTimer.current)
            clearTimeout(holdTimer.current);
        holdTimer.current = null;
        holdStart.current = null;
    };
    const cancelLinger = () => {
        if (lingerTimer.current)
            clearTimeout(lingerTimer.current);
        lingerTimer.current = null;
    };
    // Unmount: drop any pending request, start the skip window if open.
    useEffect(() => () => {
        if (holdTimer.current)
            clearTimeout(holdTimer.current);
        if (lingerTimer.current)
            clearTimeout(lingerTimer.current);
        group.release(member);
    }, [group, member]);
    // A title that empties while open closes the bubble (AntD never shows an empty one).
    useEffect(() => {
        if (empty && phase !== 'closed') {
            member.hide();
            group.release(member);
        }
    }, [empty, phase, group, member]);
    // The owning overlay opened: its popup has taken the space.
    useEffect(() => {
        if (!ownerPopupOpen)
            return;
        group.cancel(member);
        if (phaseRef.current !== 'closed') {
            member.hide();
            group.release(member);
        }
    }, [ownerPopupOpen, group, member]);
    const rawChild = Children.only(children);
    const child = (isValidElement(rawChild) && rawChild.type !== Fragment ? rawChild : _jsx("span", { children: rawChild }));
    useDevAssertTriggerResolves(antdRef, child);
    const canOpen = () => {
        const el = member.triggerEl();
        if (empty || !el || ownerPopupOpen)
            return false;
        // A hand-rolled popup trigger whose popup is open. `aria-expanded` alone
        // never suppresses: a disclosure (the sider toggle) is expanded with
        // nothing covering it.
        const haspopup = el.getAttribute('aria-haspopup');
        if (haspopup && haspopup !== 'false' && el.getAttribute('aria-expanded') === 'true')
            return false;
        return shouldOpen ? shouldOpen(el) : true;
    };
    const onOpenChange = (next) => {
        if (next) {
            if (suppressed.current || pinned.current)
                return;
            if (inputModality.lastPointerType !== 'mouse')
                return;
            const el = member.triggerEl();
            if (!canOpen() || !el)
                return;
            restAnchor.current = pointerAt.current;
            group.request(member, kind, { large: isLargeTrigger(el) });
        }
        else if (!pinned.current) {
            // Hover-out after the grace period, and Esc via the portal stack.
            close();
        }
    };
    const p = child.props;
    /** The hold ran its course: open pinned, and swallow the click the lift produces. */
    const onHold = () => {
        holdTimer.current = null;
        holdStart.current = null;
        if (!canOpen())
            return;
        longPressed.current = true;
        group.openNow(member, 'pin');
        pinned.current = true;
        group.pin(member);
    };
    /** The finger lifted (or the browser took the gesture): let a long-pressed bubble linger, then close. */
    const onLift = () => {
        cancelHold();
        if (!longPressed.current)
            return;
        cancelLinger();
        lingerTimer.current = setTimeout(() => {
            lingerTimer.current = null;
            close();
        }, LONG_PRESS_LINGER_MS);
    };
    const handlers = {
        'data-tooltip-state': phase === 'closed' || empty ? 'closed' : phase,
        // A hook for the touch styles (no callout, no text selection on a hold).
        'data-long-press': holdable && !empty ? '' : undefined,
        onPointerLeave: (event) => {
            p.onPointerLeave?.(event);
            suppressed.current = false;
            pointerAt.current = null;
            // rc-trigger never reports `false` for a bubble that never opened.
            group.cancel(member);
        },
        onPointerMove: (event) => {
            p.onPointerMove?.(event);
            const at = { x: event.clientX, y: event.clientY };
            if (holdStart.current && distance(at, holdStart.current) > LONG_PRESS_SLOP_PX)
                cancelHold();
            if (event.pointerType !== 'mouse')
                return;
            pointerAt.current = at;
            if (!group.isPending(member))
                return;
            // Still gliding: the cold wait starts again from here.
            if (!restAnchor.current)
                restAnchor.current = at;
            else if (distance(at, restAnchor.current) > REST_TOLERANCE_PX) {
                restAnchor.current = at;
                group.rearm(member);
            }
        },
        onPointerDown: (event) => {
            p.onPointerDown?.(event);
            // A new press: whatever an earlier hold left behind no longer applies.
            longPressed.current = false;
            // Dismiss on activate: pressing the control answers the question the
            // label was answering.
            if (!tapToPin && (event.pointerType === 'mouse' || !event.pointerType)) {
                suppressed.current = true;
                close();
                return;
            }
            if (holdable && !empty && (event.pointerType === 'touch' || event.pointerType === 'pen')) {
                cancelHold();
                if (lingerTimer.current) {
                    // A press on a trigger whose long-pressed bubble is still lingering:
                    // the label was read and this press acts, so the bubble goes now —
                    // otherwise the lost linger would leave it pinned until a tap elsewhere.
                    cancelLinger();
                    close();
                }
                holdStart.current = { x: event.clientX, y: event.clientY };
                holdTimer.current = setTimeout(onHold, LONG_PRESS_MS);
            }
        },
        onPointerUp: (event) => {
            p.onPointerUp?.(event);
            onLift();
        },
        onPointerCancel: (event) => {
            p.onPointerCancel?.(event);
            onLift();
        },
        onContextMenu: (event) => {
            p.onContextMenu?.(event);
            // A touch hold is ours, not the browser's context menu / callout.
            if (holdTimer.current || longPressed.current)
                event.preventDefault();
        },
        onClick: (event) => {
            if (longPressed.current) {
                // The lift after a hold: the user asked what this is, not to do it.
                longPressed.current = false;
                event.preventDefault();
                event.stopPropagation();
                return;
            }
            p.onClick?.(event);
            if (!tapToPin || !canOpen())
                return;
            const touchLike = inputModality.lastPointerType !== 'mouse';
            const pinNow = touchLike || clickToPin;
            if (pinNow && pinned.current) {
                close(); // second tap / click
                return;
            }
            group.openNow(member, pinNow ? 'pin' : 'pointer');
            if (pinNow) {
                pinned.current = true;
                group.pin(member);
            }
        },
        onFocus: (event) => {
            p.onFocus?.(event);
            if (inputModality.lastInput === 'nav-key' && canOpen())
                group.openNow(member, 'keyboard');
        },
        onBlur: (event) => {
            p.onBlur?.(event);
            suppressed.current = false;
            close(); // a pin lasts one focus visit
        },
        onKeyDown: (event) => {
            p.onKeyDown?.(event);
            longPressed.current = false;
            if (!tapToPin && (event.key === 'Enter' || event.key === ' ')) {
                suppressed.current = true;
                close();
            }
        },
    };
    const open = !empty && phase !== 'closed';
    const content = empty ? undefined : kind === 'label' ? _jsx("span", { "aria-hidden": "true", children: title }) : title;
    return (_jsx(AntdTooltip, { ...rest, ref: antdRef, rootClassName: rootClassName ? `bk-tip ${rootClassName}` : 'bk-tip', title: content, placement: resolvePlacement(placement, dir), trigger: ['hover'], 
        // The group owns the open delay; rc-trigger only reports the hover.
        mouseEnterDelay: 0, mouseLeaveDelay: group.timing.closeDelayMs / 1000, open: open, onOpenChange: onOpenChange, 
        // Cold opens animate (clamped by the reduced-motion rule); warm opens
        // and every close are instant.
        motion: phase === 'delayed-open' ? undefined : NO_MOTION, destroyOnHidden: true, children: cloneElement(child, handlers) }));
}
export function Popover({ placement = 'bottom-start', onOpenChange, content, title, ...rest }) {
    const dir = useUiDirection();
    const [open, setOpen] = useMirroredOpen(rest.open, rest.defaultOpen);
    return (_jsx(PopupTriggerProvider, { value: { open, haspopup: 'dialog', clickTrigger: isClickTrigger(rest.trigger, 'hover') }, children: _jsx(AntdPopover, { ...rest, content: isolateRender(content), title: isolateRender(title), placement: resolvePlacement(placement, dir), onOpenChange: (next) => {
                setOpen(next);
                onOpenChange?.(next);
            } }) }));
}
/**
 * Inline confirmation. The default glyph is the registry's warning rather than
 * AntD's `ExclamationCircleFilled`, and a destructive confirm (`okButtonProps.
 * danger`) gets the stronger `danger` glyph — so "archive this draft" and
 * "delete this patient" do not look identical.
 */
export function Popconfirm({ placement = 'top', icon, onOpenChange, title, description, ...rest }) {
    const dir = useUiDirection();
    const [open, setOpen] = useMirroredOpen(rest.open, rest.defaultOpen);
    const destructive = Boolean(rest.okButtonProps?.danger);
    return (_jsx(PopupTriggerProvider, { value: { open, haspopup: 'dialog', clickTrigger: isClickTrigger(rest.trigger, 'click') }, children: _jsx(AntdPopconfirm, { ...rest, title: isolateRender(title), description: isolateRender(description), icon: icon ?? _jsx(Icon, { name: destructive ? 'danger' : 'warning', size: 16 }), placement: resolvePlacement(placement, dir), onOpenChange: (next) => {
                setOpen(next);
                onOpenChange?.(next);
            } }) }));
}
//# sourceMappingURL=overlays.js.map